import React, { useEffect, useMemo, useState } from "react";
import {
  ClipboardList,
  PackageCheck,
  Key,
  Truck,
  Loader2,
  AlertCircle,
  CircleCheck,
  ArrowUpRight,
} from "lucide-react";
import { decryptData } from "../utils/storageCrypto";
import { theme } from "../assets/theme/theme";
import SwipeButton from "./components/SwipeButton";
import PackageIcon from '../assets/icons/Package.svg';

/* -------------------- MULTI-COLOR GOOGLE MAPS ICON -------------------- */
const GoogleMapsIcon = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 48 48"
    className={className}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M24 4C15.163 4 8 11.163 8 20c0 10.5 13.5 22.5 15.2 23.9.47.39 1.13.39 1.6 0C26.5 42.5 40 30.5 40 20c0-8.837-7.163-16-16-16z"
      fill="#4285F4"
    />
    <path
      d="M24 4C15.163 4 8 11.163 8 20c0 4.1 1.5 7.8 4 10.6l12-10.6V4z"
      fill="#EA4335"
    />
    <path
      d="M24 20l-12 10.6c2.7 3.1 6.5 6.8 12 13.3V20z"
      fill="#FBBC05"
    />
    <path
      d="M24 20v23.9c5.5-6.5 9.3-10.2 12-13.3L24 20z"
      fill="#34A853"
    />
    <circle cx="24" cy="20" r="7" fill="#FFFFFF" />
  </svg>
);

const STATUS = {
  READY: "ready",
  PENDING: "pending",
  IN_TRANSIT: "in_transit",
  OTP: "otp",
  DONE: "done",
};

const ActiveDropoffStep = ({ step, totalStepsCount, onComplete, activeRide, isLastStep }) => {
  console.log("ActiveDropoffStep rendered with props:", { step, totalStepsCount, activeRide });
  const [status, setStatus] = useState(STATUS.READY);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const navigateUrl = useMemo(() => buildNavigateUrl(step), [step]);

  /* Calculate dynamic step milestone label (D1, D2...) */
  const stepMilestoneLabel = useMemo(() => {
    if (!activeRide?.steps?.length) return `D${step?.step || 1}`;
    let count = 0;
    for (const s of activeRide.steps) {
      if (s.type === "DROPOFF") count += 1;
      if (s.step === step?.step || s.dg_id === step?.dg_id) break;
    }
    return `D${count || 1}`;
  }, [activeRide, step]);

useEffect(() => {
    if (!step) return;

    const currentStatus = step.status ? step.status.toUpperCase() : "";

    switch (currentStatus) {
      case "OTP_GENERATED":
        setStatus(STATUS.OTP);
        break;
      case "COMPLETED":
      case "SKIPPED":
        setStatus(STATUS.DONE);
        break;
      case "STARTED":
      case "IN_PROGRESS":
        setStatus(STATUS.IN_TRANSIT);
        break;
      case "PENDING":
        setStatus(STATUS.READY);
        break;
      default:
        setStatus((prev) =>
          prev === STATUS.IN_TRANSIT ? STATUS.IN_TRANSIT : STATUS.READY
        );
    }
  }, [step]);

  const handleOpenMaps = () => {
    if (navigateUrl) {
      window.open(navigateUrl, "_blank");
    } else {
      alert("Address or coordinates not available for navigation.");
    }
  };

/*   const handleStartRide = () => {
    setStatus(STATUS.IN_TRANSIT);
    if (navigateUrl) {
      window.open(navigateUrl, "_blank");
    }
    handleStart();
  }; */

  const handleStartRide = async () => {
    try {
      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      await fetch(`${URL}delivery/start-trip`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
        body: JSON.stringify({
          trip_id: activeRide.tripId,
          leg_id: step.leg_id
        }),
      });
    } catch (error) {
      console.error("Failed to start dropoff route leg:", error);
    }

    setStatus(STATUS.IN_TRANSIT);
    if (navigateUrl) {
      window.open(navigateUrl, "_blank");
    }
  };

  const handleReached = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      const res = await fetch(
        `${URL}customer/salesorder/generate-order-delivered-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
          body: JSON.stringify({
            trip_id: activeRide.tripId,
            step_number: step.step,
            DG_id: step.dg_id,
          }),
        }
      );

      const data = await res.json();
      if (!data.success)
        throw new Error(data.message || "Failed to generate delivery OTP");

      setStatus(STATUS.OTP);
    } catch (err) {
      setErrorMessage(err.message || "Failed to generate delivery OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      const res = await fetch(
        `${URL}customer/salesorder/verify-order-delivered-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
          body: JSON.stringify({
            trip_id: activeRide.tripId,
            step_number: step.step,
            otp: Number(otp),
            DG_id: step.dg_id,
          }),
        }
      );

      const data = await res.json();
      if (!data.success)
        throw new Error(data.message || "OTP verification failed");

      setStatus(STATUS.DONE);
      setShowSuccessModal(true);
    } catch (err) {
      setErrorMessage(err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeSuccess = () => {
    setShowSuccessModal(false);
    onComplete?.();
  };

  return (
    <div
      className="bg-white flex flex-col border shadow-sm overflow-hidden relative"
      style={{
        borderRadius: `${theme.radius[8]}px`,
        borderColor: theme.colors.grey002,
      }}
    >
      {/* Confirmation Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div
            className="w-full max-w-sm bg-white rounded-3xl p-6 text-center shadow-2xl border flex flex-col items-center space-y-4 animate-scaleUp"
            style={{
              borderColor: theme.colors.grey002,
              borderRadius: `${theme.radius[24]}px`,
            }}
          >
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center border shadow-sm"
              style={{
                backgroundColor: `${theme.colors.success}15`,
                borderColor: `${theme.colors.success}30`,
              }}
            >
              <CircleCheck
                className="w-9 h-9"
                style={{ color: theme.colors.success }}
              />
            </div>

            <div className="space-y-1">
              <h3
                className="text-xl font-bold leading-tight"
                style={{
                  fontFamily: "BarlowCondensed-Bold",
                  color: theme.colors.bitumen,
                }}
              >
                Delivery Confirmed!
              </h3>
              <p
                className="text-xs"
                style={{
                  fontFamily: "DMSans-Regular",
                  color: theme.colors.concrete,
                }}
              >
                Order <span className="font-bold text-black">{step.refNumber}</span> items have been delivered to customer.
              </p>
            </div>

            <button
              type="button"
              onClick={handleAcknowledgeSuccess}
              className="w-full py-3.5 text-white font-bold transition-all shadow-md active:scale-[0.99] flex items-center justify-center gap-2 cursor-pointer"
              style={{
                fontFamily: "DMSans-Medium",
                borderRadius: `${theme.radius[16]}px`,
                backgroundColor: theme.colors.brand,
                boxShadow: `0 4px 14px ${theme.colors.brand}40`,
              }}
            >
              <span>{isLastStep ? "Complete Trip" : "Start Route"}</span>
            </button>
          </div>
        </div>
      )}

      {/* 1. Header: Order Number (Left) & Milestone Stage + Node (Right) */}
      <div
        className="px-3.5 py-3 border-b flex items-center justify-between"
        style={{
          borderColor: theme.colors.grey005,
          backgroundColor: theme.colors.grey001,
        }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border"
            style={{
              backgroundColor: theme.colors.offWhite,
              borderColor: theme.colors.grey005,
            }}
          >
            <Truck className="w-4 h-4 text-black" />
          </div>
          <div className="min-w-0">
            <h4
              className="text-xs font-bold leading-tight"
              style={{
                fontFamily: "DMSans-SemiBold",
                color: theme.colors.concrete,
              }}
            >
              Order No:
              <span
                className="ml-1.5 text-sm font-bold leading-tight"
                style={{
                  fontFamily: "BarlowCondensed-SemiBold",
                  color: theme.colors.bitumen,
                }}
              >
                {step.refNumber}
              </span>
            </h4>
            {step.refNumber && (
              <p
                className="text-[10px] mt-0.5"
                style={{
                  fontFamily: "DMMono-Regular",
                  color: theme.colors.concrete,
                }}
              >
                Ask customer for 4-digit code to complete delivery.
              </p>
            )}
          </div>
        </div>

        {/* Right side: Stage Label + Compact Circle Node */}
        <div className="flex flex-col items-center gap-0.5 shrink-0 pl-2">
          <span
            className="text-[9px] font-bold uppercase tracking-wider leading-none"
            style={{
              fontFamily: "DMSans-Medium",
              color: theme.colors.concrete,
            }}
          >
            Drop-off
          </span>
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-[11px] shadow-2xs"
            style={{
              fontFamily: "DMMono-Medium",
              backgroundColor: theme.colors.brand,
              color: theme.colors.pureWhite,
              border: `1.5px solid ${theme.colors.brand}`,
            }}
          >
            {stepMilestoneLabel}
          </div>
        </div>
      </div>

      {/* 2. Location Section with Integrated Google Maps Action */}
      <div
        className="p-3.5 border-b flex items-start gap-3"
        style={{
          borderColor: theme.colors.grey005,
          backgroundColor: theme.colors.pureWhite,
        }}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border mt-0.5"
          style={{
            backgroundColor: theme.colors.offWhite,
            borderColor: theme.colors.grey005,
          }}
        >
          <Truck className="w-4 h-4 text-black" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h5
              className="font-bold text-xs tracking-wide"
              style={{
                fontFamily: "DMSans-SemiBold",
                color: theme.colors.concrete,
              }}
            >
              Drop-off Location
            </h5>

            {/* Maps Pill Shortcut */}
            <button
              type="button"
              onClick={handleOpenMaps}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-all active:scale-95 shadow-2xs shrink-0 cursor-pointer"
              style={{
                borderColor: theme.colors.grey002,
                backgroundColor: theme.colors.offWhite,
              }}
              title="Open location in Google Maps"
            >
              <GoogleMapsIcon className="w-3.5 h-3.5" />
              <span
                className="text-[11px] font-bold"
                style={{
                  fontFamily: "DMSans-SemiBold",
                  color: theme.colors.bitumen,
                }}
              >
                Maps
              </span>
              <ArrowUpRight className="w-3 h-3 text-gray-400" />
            </button>
          </div>

          <p
            className="text-xs font-semibold leading-relaxed mt-1 whitespace-normal break-words"
            style={{
              fontFamily: "DMSans-Medium",
              color: theme.colors.bitumen,
            }}
          >
            {step?.address || "Customer delivery location address not available"}
          </p>
        </div>
      </div>

      {/* 3. Main Content: Manifest & OTP */}
      <div className="p-3.5 space-y-3.5">
        {/* Items Manifest */}
        <div
          className="space-y-2"
          style={{
            backgroundColor: theme.colors.pureWhite,
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border"
                style={{
                  backgroundColor: theme.colors.offWhite,
                  borderColor: theme.colors.grey005,
                }}
              >
                <ClipboardList className="w-4 h-4 text-black" />
              </div>
              <span
                className="font-bold text-xs tracking-wide"
                style={{
                  fontFamily: "DMSans-SemiBold",
                  color: theme.colors.concrete,
                }}
              >
                Items to handover: {(step?.items || []).length}
              </span>
            </div>
          </div>

          <div
            className="border rounded-lg max-h-[160px] overflow-y-auto divide-y"
            style={{
              borderColor: theme.colors.grey005,
              backgroundColor: theme.colors.grey001,
            }}
          >
            {(step?.items || []).map((item, idx) => {
  const isLastItem = idx === (step.items.length - 1);
  const imageUrl =
    item.item_image || item.image
      ? `${process.env.REACT_APP_IMAGE_URL || ""}${item.item_image || item.image}`
      : null;

  return (
    <div
      key={item.itemId || item.item_id || idx}
      className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${
        !isLastItem ? "border-b" : ""
      }`}
      style={{
        borderColor: theme.colors.grey005,
      }}
    >
      {/* Product Image with Fallback */}
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border overflow-hidden"
        style={{
          backgroundColor: theme.colors.offWhite,
          borderColor: theme.colors.grey005,
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={item.name || item.item_name || "Product"}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              if (e.currentTarget.nextElementSibling) {
                e.currentTarget.nextElementSibling.style.display = "flex";
              }
            }}
          />
        ) : null}
        <div
          className="w-full h-full items-center justify-center"
          style={{ display: imageUrl ? "none" : "flex" }}
        >
          <img
            src={PackageIcon}
            alt="Package"
            className="w-5 h-5 object-contain opacity-40"
          />
        </div>
      </div>

      {/* Details Column */}
      <div className="min-w-0 flex-1 flex flex-col justify-center">
        {/* Top Row: Name & Total Price */}
        <div className="flex items-start justify-between gap-2">
          <p
            className="text-xs font-semibold leading-snug line-clamp-2"
            style={{
              fontFamily: "DMSans-Medium",
              color: theme.colors.bitumen,
            }}
          >
            {item.name || item.item_name}
          </p>

          {(item.total || item.price) && (
            <span
              className="text-xs font-bold shrink-0 text-right"
              style={{
                fontFamily: "DMMono-Medium",
                color: theme.colors.bitumen,
              }}
            >
              ₹{Number(item.total || item.price).toLocaleString("en-IN")}
            </span>
          )}
        </div>

        {/* Bottom Row: Metadata (Quantity & UOM) */}
        <p
          className="text-[11px] font-medium mt-0.5"
          style={{
            fontFamily: "DMMono-Regular",
            color: theme.colors.concrete,
          }}
        >
          Qty: <span className="font-bold text-gray-800">{item.quantity}</span> {item.uom || item.UOM}
        </p>
      </div>
    </div>
  );
})}
          </div>
        </div>

        {/* Full Bleed Separator */}
        {status === STATUS.OTP && (
          <div
            className="-mx-3.5 border-t"
            style={{ borderColor: theme.colors.grey005 }}
          />
        )}

        {/* OTP Input Section */}
        {status === STATUS.OTP && (
          <div
            className="p-3.5 rounded-xl border space-y-2.5"
            style={{
              borderColor: `${theme.colors.brand}40`,
              backgroundColor: `${theme.colors.brand}05`,
            }}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-6 h-6 rounded-md flex items-center justify-center border"
                style={{
                  backgroundColor: theme.colors.offWhite,
                  borderColor: theme.colors.grey005,
                }}
              >
                <Key className="w-3.5 h-3.5 text-black" />
              </div>
              <div>
                <h5
                  className="font-bold text-xs"
                  style={{
                    fontFamily: "DMSans-SemiBold",
                    color: theme.colors.concrete,
                  }}
                >
                  Enter Delivery OTP
                </h5>
                <p
                  className="text-[10px]"
                  style={{
                    fontFamily: "DMSans-Regular",
                    color: theme.colors.concrete,
                  }}
                >
                  Ask customer for the 4-digit completion code
                </p>
              </div>
            </div>

            <input
              maxLength={4}
              value={otp}
              onChange={(e) =>
                /^[0-9]{0,4}$/.test(e.target.value) && setOtp(e.target.value)
              }
              className="border-2 rounded-xl w-full p-2.5 text-center text-xl font-bold tracking-widest outline-none transition-all"
              style={{
                fontFamily: "DMMono-Medium",
                borderColor:
                  otp.length === 4
                    ? theme.colors.brand
                    : theme.colors.grey002,
                backgroundColor: theme.colors.pureWhite,
                color: theme.colors.bitumen,
              }}
              placeholder="----"
              inputMode="numeric"
              autoFocus
            />
          </div>
        )}

        {errorMessage && (
          <div
            className="p-2.5 rounded-xl border flex items-center gap-2"
            style={{
              backgroundColor: theme.colors.brandTint,
              borderColor: `${theme.colors.error}40`,
              color: theme.colors.error,
            }}
          >
            <AlertCircle className="w-3.5 h-3.5 shrink-0 text-black" />
            <p className="text-[11px] font-medium leading-tight">{errorMessage}</p>
          </div>
        )}

        {status === STATUS.DONE && (
          <div
            className="flex items-center gap-2.5 p-3 rounded-xl border font-bold text-xs"
            style={{
              backgroundColor: `${theme.colors.success}10`,
              borderColor: `${theme.colors.success}30`,
              color: theme.colors.success,
            }}
          >
            <PackageCheck className="w-5 h-5 text-black" />
            <span>Delivery Completed</span>
          </div>
        )}

        {/* Action Controls */}
        <div className="pt-1 flex flex-col items-center w-full">
          {status === STATUS.READY && (
            <SwipeButton
              text="Swipe to Start Route"
              activeText="Starting Route..."
              onSwipeSuccess={handleStartRide}
            />
          )}

          {status === STATUS.IN_TRANSIT && (
            <button
              type="button"
              onClick={handleReached}
              disabled={loading}
              className="w-full py-3.5 text-white rounded-xl font-bold transition-all shadow-md active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
              style={{
                fontFamily: "DMSans-Medium",
                backgroundColor: theme.colors.brand,
                boxShadow: `0 4px 12px ${theme.colors.brand}40`,
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>Generating OTP...</span>
                </>
              ) : (
                <span>Reached Drop-off Location</span>
              )}
            </button>
          )}

          {status === STATUS.OTP && (
            <button
              disabled={otp.length !== 4 || loading}
              onClick={handleVerifyOTP}
              type="button"
              className="w-full py-3.5 rounded-xl font-bold transition-all shadow-md active:scale-[0.99] flex items-center justify-center gap-2 disabled:cursor-not-allowed cursor-pointer"
              style={{
                fontFamily: "DMSans-Medium",
                backgroundColor:
                  otp.length === 4 && !loading
                    ? theme.colors.brand
                    : theme.colors.grey002,
                color:
                  otp.length === 4 && !loading
                    ? theme.colors.pureWhite
                    : theme.colors.concrete,
                boxShadow:
                  otp.length === 4 && !loading
                    ? `0 4px 12px ${theme.colors.brand}40`
                    : "none",
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>Verifying...</span>
                </>
              ) : (
                <span>Verify OTP & Complete</span>
              )}
            </button>
          )}

          {/* Help Button */}
          <button 
            onClick={() => window.location.href = "tel:+919876543210"} 
            className="mt-4 text-xs font-bold underline transition-colors cursor-pointer"
            style={{ color: theme.colors.brand, fontFamily: "DMSans-Medium" }}
          >
            Need Help? Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

const buildNavigateUrl = (step) => {
  const lat = step?.latitude;
  const lng = step?.longitude;

  if (lat && lng) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }

  if (step?.address || step?.name) {
    const query = encodeURIComponent(step.address || step.name);
    return `https://www.google.com/maps/dir/?api=1&destination=${query}`;
  }

  return null;
};

export default ActiveDropoffStep;